<template>
    <div
        v-if="hasMedia"
        class="structure-slot-hero-banner relative w-full overflow-hidden 2xl:max-w-[1440px] 2xl:mx-auto flex items-center"
        :style="containerStyle"
    >
        <!-- Background Images -->
        <div class="absolute inset-0">
            <!-- Desktop Image - shows on desktop, and tablet if no tablet image -->
            <MiscImage
                v-if="mediaDesktop"
                :lazy-load="!shouldPreload"
                :loading="!shouldPreload"
                :preload="shouldPreload ? { fetchPriority: 'high' } : false"
                provider="none"
                :src="mediaDesktop.url"
                :srcset="getImageSrcset(mediaDesktop)"
                :alt="altTextDesktop"
                :title="mediaDesktop.title"
                fit="cover"
                class="absolute inset-0 w-full h-full object-cover"
                :class="hasTabletMedia ? 'hidden lg:block' : 'hidden md:block'"
            />

            <!-- Tablet Image - only shows if configured -->
            <MiscImage
                v-if="hasTabletMedia"
                :lazy-load="!shouldPreload"
                :loading="!shouldPreload"
                :preload="shouldPreload ? { fetchPriority: 'high' } : false"
                provider="none"
                :src="mediaTablet.url"
                :srcset="getImageSrcset(mediaTablet)"
                :alt="altTextTablet"
                :title="mediaTablet.title"
                fit="cover"
                class="hidden md:block lg:hidden absolute inset-0 w-full h-full object-cover"
            />

            <!-- Mobile Image - shows on mobile, or desktop if no mobile image -->
            <MiscImage
                v-if="hasMobileMedia || mediaDesktop"
                :lazy-load="!shouldPreload"
                :loading="!shouldPreload"
                :preload="shouldPreload ? { fetchPriority: 'high' } : false"
                provider="none"
                :src="(mediaMobile || mediaDesktop).url"
                :srcset="getImageSrcset(mediaMobile || mediaDesktop)"
                :alt="altTextMobile"
                :title="(mediaMobile || mediaDesktop).title"
                fit="cover"
                class="block md:hidden absolute inset-0 w-full h-full object-cover"
            />
        </div>

        <!-- Overlay Gradient -->
        <div
            v-if="overlayColor"
            class="absolute inset-0 md:max-w-[600px] lg:max-w-[842px]"
            :style="overlayStyle"
        />

        <!-- Content -->
        <div
            v-if="hasContent"
            class="relative max-w-[232px] sm:max-w-[500px] lg:max-w-[768px] z-10 flex flex-col justify-center p-4 md:p-6 lg:py-12 lg:pr-12 lg:pl-14 2xl:pl-4"
        >
            <div class="">
                <!-- Preheadline -->
                <div
                    v-if="preheadline"
                    class="mb-4 prose max-w-none prose-headings:mb-0 prose-headings:text-headline [&_*]:text-headline [&_p]:text-lg [&_b]:font-semibold [&_strong]:font-semibold"
                    v-html="preheadline"
                />

                <!-- Headline -->
                <div
                    v-if="headline"
                    :class="headlineFontClass"
                    class="mb-4 prose max-w-none prose-headings:mb-0 prose-headings:text-secondary [&_*]:text-secondary [&_b]:font-semibold [&_strong]:font-semibold"
                    v-html="headline"
                />

                <!-- Text -->
                <div
                    v-if="text"
                    class="prose max-w-none text-lg text-primary-content mb-10 font-normal"
                    v-html="text"
                />

                <!-- Buttons -->
                <div class="flex flex-wrap gap-4">
                    <MiscLink
                        v-if="button1Text && button1Url"
                        :to="button1Url"
                        class="btn btn-md text-lg leading-[20px] font-semibold py-[10px] max-h-11"
                        :style="button1Style"
                    >
                        {{ button1Text }}
                    </MiscLink>

                    <MiscLink
                        v-if="button2Text && button2Url"
                        :to="button2Url"
                        class="btn btn-md text-lg leading-[20px] font-semibold py-[10px] max-h-11"
                        :style="button2Style"
                    >
                        {{ button2Text }}
                    </MiscLink>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { HblSlot } from '@/utils/types'
import { computed } from 'vue'
import { parseDynamicUrl } from '@/utils/helper'

const props = defineProps<{
    content: HblSlot
    count?: number
}>()

const getConfig = computed(() => props?.content?.config)
const getData = computed(() => props?.content?.data)

// Determine if this element should be preloaded (LCP optimization)
const shouldPreload = computed(() => {
    const isLCPOptimized = getConfig.value?.isLCPOptimized?.value
    return isLCPOptimized
})

// Media entities from resolver
const mediaDesktop = computed(() => getData.value?.mediaDesktop?.media)
const mediaTablet = computed(() => getData.value?.mediaTablet?.media)
const mediaMobile = computed(() => getData.value?.mediaMobile?.media)

const hasMedia = computed(() => !!mediaDesktop.value)

// Check if media actually has URL (to handle empty objects)
const hasTabletMedia = computed(() => !!(mediaTablet.value && mediaTablet.value.url))
const hasMobileMedia = computed(() => !!(mediaMobile.value && mediaMobile.value.url))

// Heights per viewport
const heightDesktop = computed(() => getConfig.value?.bannerHeightDesktop?.value || '600px')
const heightTablet = computed(() => getConfig.value?.bannerHeightTablet?.value || '500px')
const heightMobile = computed(() => getConfig.value?.bannerHeightMobile?.value || '400px')


const containerStyle = computed(() => ({
    '--height-mobile': heightMobile.value,
    '--height-md': heightTablet.value,
    '--height-lg': heightDesktop.value
}))

// Accessibility
const isDecorative = computed(() => getConfig.value?.decorative?.value || false)

// Alt text for each viewport - uses custom altText or the specific media's alt
const altTextDesktop = computed(() => {
    if (isDecorative.value) return ''
    return getConfig.value?.altText?.value || mediaDesktop.value?.alt || ''
})

const altTextTablet = computed(() => {
    if (isDecorative.value) return ''
    const media = hasTabletMedia.value ? mediaTablet.value : mediaDesktop.value
    return getConfig.value?.altText?.value || media?.alt || ''
})

const altTextMobile = computed(() => {
    if (isDecorative.value) return ''
    const media = hasMobileMedia.value ? mediaMobile.value : mediaDesktop.value
    return getConfig.value?.altText?.value || media?.alt || ''
})

// Overlay
const overlayColor = computed(() => {
    const color = getConfig.value?.overlayColor?.value
    return color && color !== 'transparent' ? color : null
})

const overlayStyle = computed(() => {
    if (!overlayColor.value) return {}

    const color = overlayColor.value
    return {
        background: `linear-gradient(269deg, transparent 0%, ${color} 48.56%, ${color} 71.15%, ${color} 81.73%, ${color} 90.03%)`
    }
})

// Content
const preheadline = computed(() => getConfig.value?.preheadline?.value)
const headline = computed(() => getConfig.value?.headline?.value)
const text = computed(() => getConfig.value?.text?.value)

const hasContent = computed(() => !!(preheadline.value || headline.value || text.value))

// Headline styling
const headlineFont = computed(() => getConfig.value?.headlineFont?.value || 'source-sans')

const headlineFontClass = computed(() => {
    return headlineFont.value === 'botanica-sans' ? 'font-decorative' : ''
})

// Buttons
const button1Text = computed(() => getConfig.value?.button1Text?.value)
const button1Url = computed(() => parseDynamicUrl(getConfig.value?.button1Url?.value))
const button1BgColor = computed(() => getConfig.value?.button1BgColor?.value)
const button1TextColor = computed(() => getConfig.value?.button1TextColor?.value)

const button1Style = computed(() => {
    const styles: Record<string, string> = {}
    if (button1BgColor.value) styles.backgroundColor = button1BgColor.value
    if (button1BgColor.value) styles.borderColor = button1BgColor.value
    if (button1TextColor.value) styles.color = button1TextColor.value
    return styles
})

const button2Text = computed(() => getConfig.value?.button2Text?.value)
const button2Url = computed(() => parseDynamicUrl(getConfig.value?.button2Url?.value))
const button2BgColor = computed(() => getConfig.value?.button2BgColor?.value)
const button2TextColor = computed(() => getConfig.value?.button2TextColor?.value)

const button2Style = computed(() => {
    const styles: Record<string, string> = {}
    if (button2BgColor.value) styles.backgroundColor = button2BgColor.value
    if (button2BgColor.value) styles.borderColor = button2BgColor.value
    if (button2TextColor.value) styles.color = button2TextColor.value
    return styles
})

// Helper to generate srcset from thumbnails
const getImageSrcset = (media: any) => {
    if (!media?.thumbnails) return undefined
    return media.thumbnails
        .map((thumb: { url: string; width: number }) => `${thumb.url.replace(/\s/g, '%20')} ${thumb.width}w`)
        .join(', ')
}
</script>